import { parse } from 'tldts';

const LOOPBACK_HOSTNAMES = new Set(['localhost', '127.0.0.1', '[::1]']);

const parseOptions = { allowPrivateDomains: true as const };

/**
 * Returns the registrable domain (eTLD+1) for `hostname`, suitable for the
 * `Domain` attribute on cookies so sibling hosts (e.g. `api.benedek.ro` and
 * `benedek.ro`, or `bndk-stage….e3cloud.io` and `bndk-api-stage….e3cloud.io`)
 * receive the same cookie.
 *
 * Omits the result for localhost, loopback, IPs, and hosts with no parseable domain.
 */
export const getRegistrableCookieDomainFromHostname = (hostname: string): string | undefined => {
  const host = hostname.trim().toLowerCase();
  if (!host || LOOPBACK_HOSTNAMES.has(host)) return undefined;

  const { domain, isIp } = parse(host, parseOptions);
  if (isIp || !domain) return undefined;

  return domain;
};

const hostnameFromUrlInput = (url: string | URL): string | undefined => {
  if (url instanceof URL) return url.hostname || undefined;
  const trimmed = url.trim();
  if (!trimmed) return undefined;
  try {
    const withProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    return new URL(withProtocol).hostname || undefined;
  } catch {
    return undefined;
  }
};

/**
 * Same as {@link getRegistrableCookieDomainFromHostname}, but accepts a full URL
 * or a bare host / host:port string (a scheme is added when missing).
 */
export const getRegistrableCookieDomainFromUrl = (url: string | URL): string | undefined => {
  const hostname = hostnameFromUrlInput(url);
  if (!hostname) return undefined;
  return getRegistrableCookieDomainFromHostname(hostname);
};
